
import { UserApplicationVO } from '../../vo/user/user-app.vo';
import { Model } from '../../model/model';
import { ViewData } from '../../model/view.data';
import { RoleVO } from '../../vo/user/role.vo';
import { Permission, PermissionAccess } from './permissions.types';
import { UserVO } from '../../vo/user/user.vo';
import { FeiVO } from '../../vo/workingdata/fei.vo';
import { VisionStepPerimeter } from '@wizeoo/akm-message-lib';
import { WizMathUtils } from '@wizeoo/akm-core-lib';
import { EnvironnementManager } from '../../model/environnement.manager';

/**
 * Classe permettant la gestion des permissions de l'utilisateur.
 *
 * Cette classe expose des méthodes utiles permettant de connaître le niveau de permissions de l'utilisateur
 * pour un rôle donné
 */
export class PermissionEngine {
  
  protected _model: Model = null;
  public get model(): Model {
    return this._model as Model;
  }

  constructor(model: Model) {
    this._model = model;
  }

  
  /**
   * Indique si l'utilisateur peut créer avec la feature en paramètre
   *
   * @param featureCode Le code de la feature
   */
  public canCreate(featureCode: string, perimeter?: UserApplicationVO): boolean {
    if (EnvironnementManager.envConfig.byPassPermission) {
      return true;
    }

    // gestion des users verrouillés
    if (!!this.user.locked) {
      console.log('locked user', this.user);
      return false;
    }

    perimeter = this.resolvePerimeter(perimeter);

    if (this._model.userEngine.isGlobalDoNone(this.user, perimeter)) {
      return false;
    }

    if (this.isAdmin(perimeter)) {
      return true;
    }

    const roles: RoleVO[] = this.getFeatureRoles(featureCode, perimeter);
    return !!roles && roles.filter((r) => !r.doNone && r.doCreate).length > 0;
  }

  /**
   * Vérifie si l'utilisateur peut lire avec la feature en paramètre
   *
   * @param featureCode Le code de la feature
   */
  public canRead(featureCode: string, perimeter?: UserApplicationVO): boolean {
    if (EnvironnementManager.envConfig.byPassPermission) {
      return true;
    }

    // gestion des users verrouillés
    if (!!this.user.locked) {
      console.log('locked user', this.user);
      return false;
    }


    perimeter = this.resolvePerimeter(perimeter);

    if (this._model.userEngine.isGlobalDoNone(this.user, perimeter)) {
      console.log('canRead global do None', featureCode);
      return false;
    }

    if (this.isAdmin(perimeter)) {
      return true;
    }

    const roles: RoleVO[] = this.getFeatureRoles(featureCode, perimeter);
    // TODO voir avec la team dev si on assure la cohérence à ce niveau ou pas
    // cas particulier pour le read. si on peut créer, maj ou delete alors on peut read...
    let roleRes: boolean = !!roles && roles.filter((r) => !r.doNone && (r.doDelete || r.doCreate || r.doRead || r.doUpdate)).length > 0;
    return roleRes;
  }

  /**
   * Vérifie si l'utilisateur peut mettre à jour avec la feature en paramètre
   *
   * @param featureCode Le code de la feature
   */
  public canUpdate(featureCode: string, perimeter?: UserApplicationVO): boolean {
    if (EnvironnementManager.envConfig.byPassPermission) {
      return true;
    }
    // gestion des users verrouillés
    if (!!this.user.locked) {
      console.log('locked user', this.user);
      return false;
    }


    perimeter = this.resolvePerimeter(perimeter);

    if (this._model.userEngine.isGlobalDoNone(this.user, perimeter)) {
      return false;
    }

    if (this.isAdmin(perimeter)) {
      return true;
    }

    const roles: RoleVO[] = this.getFeatureRoles(featureCode, perimeter);
    return !!roles && roles.filter((r) => !r.doNone && r.doUpdate).length > 0;
  }

  /**
   * Vérifie si l'utilisateur peut supprimer avec la feature en paramètre
   *
   * @param featureCode Le code de la feature
   */
  public canDelete(featureCode: string, perimeter?: UserApplicationVO): boolean {
    if (EnvironnementManager.envConfig.byPassPermission) {
      return true;
    }
    // gestion des users verrouillés
    if (!!this.user.locked) {
      console.log('locked user', this.user);
      return false;
    }


    perimeter = this.resolvePerimeter(perimeter);

    if (this._model.userEngine.isGlobalDoNone(this.user, perimeter)) {
      return false;
    }

    if (this.isAdmin(perimeter)) {
      return true;
    }

    const roles: RoleVO[] = this.getFeatureRoles(featureCode, perimeter);
    return !!roles && roles.filter((r) => !r.doNone && r.doDelete).length > 0;
  }

  /**
   * Vérifie si le user a accès au périmètre en paramètres.
   *
   * Sans vérifier les droits pour une feature, il suffit que le user ne soit pas bloquer sur le périmètre ou sur un périmètre supérieur.
   *
   * Il peut aussi accéder au périmètre si il est admin
   *
   * @param perimeter Le périmètre à vérifier
   * @returns True si le user a accès. False sinon
   */
  public canAccessPerimeter(perimeter: UserApplicationVO): boolean {

    // gestion des users verrouillés
    if (!!this.user.locked) {
      console.log('locked user', this.user);
      return false;
    }

    // le user ne peut pas voir le périmètre si il n'a pas de droit sur la BU ou sur une BU de plus haut niveau
    const userApp: UserApplicationVO = this._model.userEngine.getUserApp(
      this.user,
      perimeter.domain,
      perimeter.businessUnitCode,
      perimeter.visionCode,
      perimeter.perimeter
    );

    if ((!!userApp && userApp.doNone) || (!userApp && this._model.userEngine.isGlobalDoNone(this.user, perimeter))) {
      return false;
    }

    if (!userApp && !this._model.userEngine.hasHigherUserApp(this.user, perimeter)) {
      return false;
    }

    return true;
  }

  /**
   * Vérifie si l'utilisateur possède une des permissions demandées sur la feature en paramètre
   *
   * @param featureCode Le code de la feature
   * @param permissions Les permissions à vérifier
   */
  public hasAnyPermission(permission: Permission, perimeter?: UserApplicationVO): boolean {
    if (EnvironnementManager.envConfig.byPassPermission) {
      return true;
    }

    // gestion des users verrouillés
    if (!!this.user.locked) {
      console.log('locked user', this.user);
      return false;
    }

    perimeter = this.resolvePerimeter(perimeter);

    if (this._model.userEngine.isGlobalDoNone(this.user, perimeter)) {
      return false;
    }

    if (this.isAdmin(perimeter)) {
      return true;
    }

    const roles: RoleVO[] = this.getFeatureRoles(permission.feature, perimeter);

    if (!!roles) {
      return roles.filter((r) => this.matchAny(r, permission)).length > 0;
    }

    return false;
  }

  /**
   * Vérifie si le role en paramètre possède match la permission en paramètre
   * @param role Le role à vérifier
   * @param permission La permission contenant les informations pour effectuer la vérification
   */
  public matchAny(role: RoleVO, permission: Permission): boolean {
    return (
      permission.accesses.filter((p) => {
        switch (p) {
          case PermissionAccess.CREATE:
            return !role.doNone && !!role.doCreate;
          case PermissionAccess.READ:
            return !role.doNone && (!!role.doCreate || role.doRead || role.doUpdate || role.doDelete);
          case PermissionAccess.UPDATE:
            return !role.doNone && role.doUpdate;
          case PermissionAccess.DELETE:
            return !role.doNone && role.doDelete;
          case PermissionAccess.BATCH:
            return !role.doNone && role.doBatch;
        }
      }).length > 0
    );
  }

  /**
   * Renvoie les accès autorisé pour une feature donnée
   * @param feature
   */
  public getFeatureAccesses(feature: string): PermissionAccess[] {
    if (EnvironnementManager.envConfig.byPassPermission) {
      return [PermissionAccess.READ, PermissionAccess.CREATE, PermissionAccess.UPDATE, PermissionAccess.DELETE, PermissionAccess.BATCH];
    }

    const perimeter = this.resolvePerimeter(null);

    if (this._model.userEngine.isGlobalDoNone(this.user, perimeter)) {
      return [];
    }

    if (this.isAdmin(perimeter)) {
      return [PermissionAccess.READ, PermissionAccess.CREATE, PermissionAccess.UPDATE, PermissionAccess.DELETE, PermissionAccess.BATCH];
    }

    const result = [];
    const roles: RoleVO[] = this.getFeatureRoles(feature);

    if (!!roles && roles.length > 0) {
      roles.forEach((r) => {
        if (r.doRead) {
          result.push(PermissionAccess.READ);
        }

        if (r.doCreate) {
          result.push(PermissionAccess.CREATE);
        }

        if (r.doUpdate) {
          result.push(PermissionAccess.UPDATE);
        }

        if (r.doDelete) {
          result.push(PermissionAccess.DELETE);
        }
        if (r.doBatch) {
          result.push(PermissionAccess.BATCH);
        }
      });
    }

    // petit hack pour éliminer les doublons
    return Array.from(new Set(result));
  }

  /**
   * Vérifie sur le user est admin pour la feature donnée en paramètre et le périmètre donné
   * @param feature la feature à anlyser
   * @param userApp le périmètre
   */
  private isAdmin(perimeter?: UserApplicationVO) {
    // on construit le périmètre en fonction des données du model
    if (!perimeter) {
      perimeter = new UserApplicationVO(this.model);
      perimeter.domain = this._model.domainCode;
      perimeter.businessUnitCode = this._model.businessUnitCode;
      perimeter.visionCode = this._model.visionCode;
    }

    let resBooboule: [boolean, boolean] = this._model.userEngine.isAdmin(this.user, perimeter);
    let result: boolean = false;
    if (resBooboule?.length > 0) {
      result = resBooboule[0];
    }
    return result;
  }

  /**
   * Résolution du périmètre.
   *
   * Si celui en paramètre est null, on initialise un paramètre avec les données du model
   * @param perimeter
   */
  private resolvePerimeter(perimeter: UserApplicationVO): UserApplicationVO {
    if (!perimeter) {
      return this.model.userAppEngine.newUserApp(
        this._model.domainCode,
        this._model.businessUnitCode,
        this._model.visionCode
      );
    }

    return perimeter;
  }

  /**
   * Retourne les rôles associés à une feature
   *
   * @param featureCode Le code de la feature
   */
  private getFeatureRoles(featureCode: string, perimeter?: UserApplicationVO): RoleVO[] {
    let domain: string = this._model.domainCode;
    let bu: string = this._model.businessUnitCode;
    let visionCode: string = this._model.visionCode; // forcément wildcard dans dom

    if (!!perimeter) {
      visionCode = perimeter.visionCode;
      domain = perimeter.domain;
      bu = perimeter.businessUnitCode;
    }

    return this._model.userEngine.resolveRolesForFeature(this.user, featureCode, domain, bu, visionCode);
  }

  /**
   * Méthode d'accès rapide au connected user pour alléger la lecture du code
   */
  private get user(): UserVO {
    return this._model.connectedUser;
  }

  /* ##############################################################################
              autorisations sur les FEIs / les widgets selon vs / vsp
  ############################################################################## */

  public static BIN_CREATE: number = 1;
  public static BIN_READ: number = 2;
  public static BIN_UPDATE: number = 4;
  public static BIN_DELETE: number = 8;
  public static BIN_BATCH: number = 16;
  public static BIN_DO_NONE: number = -1;


  /**
   * recherche des droits sur un fei
   * @param fei cible
   * @returns une map de somme de droit par code vs / vsp
   */
  public getFeiPermissions(fei: FeiVO): Map<string, number> {
    let result: Map<string, number> = new Map<string, number>();

    const baseCode: string = fei.visionCode + ViewData.KEY_SEPARATOR + fei.visionStepLevel;
    result.set(baseCode, this.feedFeiPermissionScore(baseCode));

    Object.values(fei.vision.visionStepPerimeters || {}).forEach((vsp: VisionStepPerimeter) => {
      if (vsp.visionStepLevel === fei.visionStepLevel) {
        const code = baseCode + ViewData.KEY_SEPARATOR + vsp.code;
        result.set(code, this.feedFeiPermissionScore(code));
      }
    })
    return result;
  }

  /**
   * alimente le score de permission pour un step / stepPerimeter
   * @param code unique du vs ou vsp
   * @returns 1, 2, 4, 8, 16, la somme de ça avec * -1 si DoNone
   */
  private feedFeiPermissionScore(code: string): number {
    let score: number = 0;
    let tmpRes: PermissionAccess[] = this.getFeatureAccesses(code); // ajout des rôles sur le step

    let negate: boolean = false; // si on trouve un doNone, on va multiplier par -1
    if (tmpRes.length > 0) {
      tmpRes.forEach(item => {
        switch (item) {
          case PermissionAccess.CREATE:
            score += 1;
            break;
          case PermissionAccess.READ:
            score += 2;
            break;
          case PermissionAccess.UPDATE:
            score += 4;
            break;
          case PermissionAccess.DELETE:
            score += 8;
            break;
          case PermissionAccess.BATCH:
            score += 16;
            break;
          case PermissionAccess.DO_NONE:
            negate = true;
            break;
        }
      });

      if (negate) {
        score = score * -1;
      }
    }
    return score;
  }

  /**
   * moteur de base des permissions de fei
   * @param fei cible
   * @param kind type de droit recherché (combo possible)
   * @returns true si ok
   */
  public feiBasePermission(fei: FeiVO, kind: number): boolean {
    let result = false;
    let lock: boolean = false; // un DoNone
    if(!!fei?.permissionMap) {
      [...fei.permissionMap.values()].forEach(score => {
        if (WizMathUtils.wizBitwize(score, kind)) {
          result = true;
        }
        if (score < 0) {
          lock = true;
        }
      });
    }

    return result && !lock;
  }

  /**
   * autoriser le delete. A minima une autorisation, et pas de lock
   * @param fei cible
   * @returns true si autorisé
   */
  public feiCanDelete(fei: FeiVO): boolean {
    return this.feiBasePermission(fei, PermissionEngine.BIN_DELETE);
  }

  /**
   * autoriser le create. A minima une autorisation, et pas de lock
   * @param fei cible
   * @returns true si autorisé
   */
  public feiCanCreate(fei: FeiVO): boolean {
    return this.feiBasePermission(fei, PermissionEngine.BIN_CREATE);
  }

  /**
   * autoriser l'update. A minima une autorisation, et pas de lock
   * @param fei cible
   * @returns true si autorisé
   */
  public feiCanUpdate(fei: FeiVO): boolean {
    return this.feiBasePermission(fei, PermissionEngine.BIN_UPDATE);
  }


  public feiCanBatch(fei: FeiVO): boolean {
    return this.feiBasePermission(fei, PermissionEngine.BIN_BATCH);
  }



}
